import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { useEffect, useState } from "react"
import api from "@/lib/axiosInstance"
import { useDispatch } from "react-redux"
import { setCredentials } from "@/lib/redux/authSlice"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { AlertCircle } from "lucide-react"
import { toast } from "sonner"
import { Spinner } from "@/components/ui/shadcn-io/spinner"

export default function ResetPassword() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [form, setForm] = useState({
        email: searchParams.get('email'),
        password: '',
        confirmPassword: ''
    });
    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const [success, setSuccess] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const navigate = useNavigate();

    const validateLink = async () => {
        try {
            var formdata = new FormData();
            formdata.append('email', form.email ?? '');
            formdata.append('token', searchParams.get('token') ?? '');
            const response = await api.post(`/agents/password/reset/validate`, formdata);
            if(!response.data.status) {
                throw new Error(response.data.error);
            }
        } catch (err : any) {
            const error = err.response ? err.response.data.error : err.message;
            navigate('/login', { state: { message: error } });
        }
    }

    const validateField = (name: string, value: string, allValues = form): string[] => {
        const errors: string[] = [];

        switch (name) {
            case 'password':
                if (!value.trim()) errors.push('password');
                else {
                    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*#?&^])[A-Za-z\d@$!%*#?&^]{6,}$/;
                    if (!passwordRegex.test(value)) errors.push('password-format');
                }
                if (allValues.confirmPassword && value !== allValues.confirmPassword) {
                    errors.push('password-match');
                }
                break;

            case 'confirmPassword':
                console.log(value, allValues.password)
                if (!value.trim()) errors.push('confirmPassword');
                else if (value !== allValues.password) errors.push('password-match');
                break;
        }

        return errors;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        setError(null);
        setSuccess(null);

        const fieldErrors = validateField(name, value, { ...form, [name]: value });
        setValidationErrors(fieldErrors);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            let allErrors: string[] = [];
            Object.keys(form).forEach(key => {
                allErrors = [...allErrors, ...validateField(key, (form as any)[key], form)];
            });
            setValidationErrors(allErrors);
    
            if (allErrors.length > 0) throw new Error('Please complete all required fields.')
    
            setSubmitting(true);
            var formdata = new FormData();
            formdata.append('email', form.email ?? '');
            formdata.append('password', form.password);
            formdata.append('token', searchParams.get('token') ?? '');

            const response = await api.post('/agents/password/reset', formdata);
            if(!response.data.status) {
                throw new Error(response.data.error);
            }
            navigate('/login', { state: { successMessage: response.data.message } });
        } catch (err: any) {
            const error = err.response ? err.response.data.error : err.message;
            setError(error);
        } finally {
            setSubmitting(false);
        }
    };

    const fieldErrorText: Record<string, string> = {
        email: 'Email is required',
        'email-format': 'Invalid email format',
        password: 'Password is required',
        'password-format': 'Password must contain uppercase, lowercase, number, special character, min 6 chars',
        'password-match': 'Passwords do not match',
        confirmPassword: 'Confirm password is required'
    };

    useEffect(() => {
        validateLink();
    }, []);

    return (
        <div className="flex items-center justify-center h-screen w-screen">
            <Card className="w-[380px] shadow-xl border-0">
                <CardHeader>
                    <CardTitle className="text-center text-2xl font-bold">Password Reset</CardTitle>
                    <CardDescription className="text-center">
                        Reset your password
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {
                        error && (
                            <Alert variant="destructive" className="mb-3 bg-red-100 border-red-300">
                                <AlertCircle className="h-4 w-4" />
                                <AlertTitle>Error</AlertTitle>
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        )
                    }
                    {
                        success && (
                            <Alert className="mb-3 bg-green-100 border-green-300">
                                <AlertCircle className="h-4 w-4" color="green"/>
                                <AlertTitle>Success</AlertTitle>
                                <AlertDescription>{success}</AlertDescription>
                            </Alert>
                        )
                    }
                   <form onSubmit={handleSubmit}>
                    {/* <div className="mb-3">
                        <Input
                        type="email"
                        name="email"
                        placeholder="Email"
                        value={form.email}
                        onChange={handleChange}
                        className={`text-sm border-gray-300`}
                        disabled/>
                    </div> */}

                    <div className="mb-3">
                        <Input
                        type="password"
                        name="password"
                        placeholder="New Password"
                        value={form.password}
                        onChange={handleChange}
                        className={`text-sm border-gray-300 ${validationErrors.includes('password') || validationErrors.includes('password-format') ? 'border-red-400' : ''}`}/>
                        {validationErrors.includes('password') && <p className="text-xs text-red-500">{fieldErrorText['password']}</p>}
                        {validationErrors.includes('password-format') && <p className="text-xs text-red-500">{fieldErrorText['password-format']}</p>}
                    </div>

                    <div className="mb-3">
                        <Input
                        type="password"
                        name="confirmPassword"
                        placeholder="Confirm Password"
                        value={form.confirmPassword}
                        onChange={handleChange}
                        className={`text-sm border-gray-300 ${validationErrors.includes('confirmPassword') || validationErrors.includes('password-match') ? 'border-red-400' : ''}`}/>
                        {validationErrors.includes('confirmPassword') && <p className="text-xs text-red-500">{fieldErrorText['confirmPassword']}</p>}
                        {validationErrors.includes('password-match') && <p className="text-xs text-red-500">{fieldErrorText['password-match']}</p>}
                    </div>

                    <Button
                    type="submit"
                    className="w-full mt-3 flex mt-5"
                    disabled={submitting} >
                        {submitting ? <Spinner /> : 'Reset Password'}
                    </Button>
                </form>
                </CardContent>
            </Card>
        </div>
    )
}
